"use client";

import { m, useInView, useAnimation, Variants } from "framer-motion";
import { useRef, useEffect, useMemo } from "react";

interface RevealProps {
  children: React.ReactNode;
  width?: "fit-content" | "100%";
  delay?: number;
  duration?: number;
  direction?: "up" | "down" | "left" | "right" | "none";
  className?: string;
  once?: boolean;
  threshold?: number;
}

export default function Reveal({
  children,
  width = "fit-content",
  delay = 0,
  duration = 0.5,
  direction = "up",
  className = "",
  once = true,
  threshold = 0.2,
}: RevealProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once, amount: threshold });
  const mainControls = useAnimation();

  useEffect(() => {
    if (isInView) {
      mainControls.start("visible");
    } else if (!once) {
      mainControls.start("hidden");
    }
  }, [isInView, once, mainControls]);

  const variants: Variants = useMemo(() => {
    const distance = 50;
    const base: Variants = { hidden: { opacity: 0 }, visible: { opacity: 1 } };

    switch (direction) {
      case "up":
        return { hidden: { opacity: 0, y: distance }, visible: { opacity: 1, y: 0 } };
      case "down":
        return { hidden: { opacity: 0, y: -distance }, visible: { opacity: 1, y: 0 } };
      case "left":
        return { hidden: { opacity: 0, x: distance }, visible: { opacity: 1, x: 0 } };
      case "right":
        return { hidden: { opacity: 0, x: -distance }, visible: { opacity: 1, x: 0 } };
      case "none":
      default:
        return base;
    }
  }, [direction]);

  return (
    <div ref={ref} style={{ width }} className={className}>
      <m.div
        variants={variants}
        initial="hidden"
        animate={mainControls}
        transition={{ duration, delay, ease: [0.25, 0.25, 0.25, 0.75] }}
      >
        {children}
      </m.div>
    </div>
  );
}
